<template>
  <b-card no-body>
    <b-card-header>
      <b-card-title>{{ $t('dashboard.projectStatistics') }}</b-card-title>
      <div class="d-flex align-items-center">
        <b-form-select
          v-model="selectedPeriod"
          :options="periodOptions"
          size="sm"
          class="mr-1"
          style="width: auto;"
          @change="onPeriodChange"
        />
        <b-form-select
          v-if="selectedPeriod === 'quarter'"
          v-model="selectedQuarter"
          :options="quarterOptions"
          size="sm"
          class="mr-1"
          style="width: auto;"
          @change="onFilterChange"
        />
        <b-form-select
          v-if="showYearSelector"
          v-model="selectedYear"
          :options="yearOptions"
          size="sm"
          class="mr-1"
          style="width: auto;"
          @change="onFilterChange"
        />
        <b-button
          v-ripple.400="'rgba(255, 255, 255, 0.15)'"
          variant="outline-secondary"
          size="sm"
          class="mr-1"
          @click="showCustomDateModal = true"
        >
          <feather-icon
            icon="CalendarIcon"
            size="16"
          />
        </b-button>
        <b-button
          v-ripple.400="'rgba(255, 255, 255, 0.15)'"
          variant="primary"
          size="sm"
          :disabled="isLoading"
          @click="refreshData"
        >
          <feather-icon
            icon="RefreshCwIcon"
            :class="{ 'animate-spin': isLoading }"
          />
        </b-button>
      </div>
    </b-card-header>
    <b-card-body v-if="projects">
      <b-row>
        <!-- Statistics Summary -->
        <b-col
          cols="12"
          class="mb-2"
        >
          <div class="d-flex justify-content-between align-items-center">
            <div>
              <h3 class="mb-0">
                {{ projects.total_in_period }}
              </h3>
              <span class="text-muted">{{ $t('dashboard.totalInPeriod') }}</span>
            </div>
            <div class="text-right">
              <h3 class="mb-0 text-success">
                {{ projects.completion_rate }}%
              </h3>
              <span class="text-muted">{{ $t('dashboard.completionRate') }}</span>
            </div>
            <div class="text-right">
              <h3 class="mb-0 text-danger">
                {{ projects.overdue_count }}
              </h3>
              <span class="text-muted">{{ $t('dashboard.overdueCount') }}</span>
            </div>
          </div>
        </b-col>

        <!-- Status Chart -->
        <b-col
          lg="4"
          md="12"
        >
          <h5 class="mb-1">
            {{ $t('dashboard.byStatus') }}
          </h5>
          <vue-apex-charts
            type="donut"
            height="250"
            :options="statusChartOptions"
            :series="statusChartSeries"
          />
        </b-col>

        <!-- Type Chart -->
        <b-col
          lg="4"
          md="12"
        >
          <h5 class="mb-1">
            {{ $t('dashboard.byType') }}
          </h5>
          <vue-apex-charts
            type="donut"
            height="250"
            :options="typeChartOptions"
            :series="typeChartSeries"
          />
        </b-col>

        <!-- Customer Type Chart -->
        <b-col
          lg="4"
          md="12"
        >
          <h5 class="mb-1">
            {{ $t('dashboard.byCustomerType') }}
          </h5>
          <vue-apex-charts
            type="donut"
            height="250"
            :options="customerTypeChartOptions"
            :series="customerTypeChartSeries"
          />
        </b-col>

        <!-- Recent Projects -->
        <b-col
          cols="12"
          class="mt-2"
        >
          <h5 class="mb-1">
            {{ $t('dashboard.recentProjects') }}
          </h5>
          <b-table
            :items="projects.recent_projects"
            :fields="tableFields"
            responsive
            class="mb-0"
            small
            striped
            hover
          >
            <template #cell(project_number)="data">
              <b-link :to="{ name: 'project-view', params: { id: data.item.id } }">
                {{ data.value }}
              </b-link>
            </template>
            <template #cell(status)="data">
              <b-badge :variant="getStatusVariant(data.value)">
                {{ $t(`project.status.${data.value}`) }}
              </b-badge>
            </template>
            <template #cell(budget)="data">
              {{ formatCurrency(data.value) }}
            </template>
          </b-table>
        </b-col>
      </b-row>
    </b-card-body>

    <!-- Loading State -->
    <b-card-body v-else-if="isLoading">
      <div class="text-center py-3">
        <b-spinner variant="primary" />
      </div>
    </b-card-body>

    <!-- Empty State -->
    <b-card-body v-else>
      <div class="text-center py-3">
        <p class="text-muted">
          {{ $t('dashboard.noDataAvailable') }}
        </p>
      </div>
    </b-card-body>

    <!-- Custom Date Range Modal -->
    <b-modal
      v-model="showCustomDateModal"
      :title="$t('dashboard.customDateRange')"
      :ok-title="$t('common.apply')"
      :cancel-title="$t('common.cancel')"
      @ok="applyCustomDateRange"
      @hidden="resetCustomDateForm"
    >
      <b-form-group :label="$t('dashboard.startDate')">
        <b-form-input
          v-model="customStartDate"
          type="date"
        />
      </b-form-group>
      <b-form-group :label="$t('dashboard.endDate')">
        <b-form-input
          v-model="customEndDate"
          type="date"
        />
      </b-form-group>
    </b-modal>
  </b-card>
</template>

<script>
import {
  BCard,
  BCardHeader,
  BCardTitle,
  BCardBody,
  BRow,
  BCol,
  BButton,
  BFormSelect,
  BTable,
  BBadge,
  BLink,
  BSpinner,
  BModal,
  BFormGroup,
  BFormInput,
} from 'bootstrap-vue'
import { mapGetters, mapActions } from 'vuex'
import Ripple from 'vue-ripple-directive'
import VueApexCharts from 'vue-apexcharts'

export default {
  name: 'ProjectStatisticsWidget',
  components: {
    BCard,
    BCardHeader,
    BCardTitle,
    BCardBody,
    BRow,
    BCol,
    BButton,
    BFormSelect,
    BTable,
    BBadge,
    BLink,
    BSpinner,
    BModal,
    BFormGroup,
    BFormInput,
    VueApexCharts,
  },
  directives: {
    Ripple,
  },
  data() {
    const currentYear = new Date().getFullYear()
    return {
      selectedPeriod: 'month',
      selectedQuarter: null,
      selectedYear: currentYear,
      showCustomDateModal: false,
      customStartDate: '',
      customEndDate: '',
      periodOptions: [
        { value: 'day', text: this.$t('dashboard.period.day') },
        { value: 'week', text: this.$t('dashboard.period.week') },
        { value: 'month', text: this.$t('dashboard.period.month') },
        { value: 'quarter', text: this.$t('dashboard.period.quarter') },
        { value: 'year', text: this.$t('dashboard.period.year') },
        { value: 'all', text: this.$t('dashboard.period.all') },
      ],
      quarterOptions: [
        { value: 1, text: this.$t('dashboard.quarter.q1') },
        { value: 2, text: this.$t('dashboard.quarter.q2') },
        { value: 3, text: this.$t('dashboard.quarter.q3') },
        { value: 4, text: this.$t('dashboard.quarter.q4') },
      ],
      tableFields: [
        { key: 'project_number', label: this.$t('project.projectNumber') },
        { key: 'name', label: this.$t('project.name') },
        { key: 'status', label: this.$t('project.status') },
        { key: 'customer_name', label: this.$t('project.customer') },
        { key: 'budget', label: this.$t('project.budget') },
      ],
    }
  },
  computed: {
    ...mapGetters('dashboard', ['projects', 'loading', 'filters']),
    isLoading() {
      return this.loading.projects
    },
    showYearSelector() {
      return ['quarter', 'year'].includes(this.selectedPeriod)
    },
    yearOptions() {
      const currentYear = new Date().getFullYear()
      const years = []
      for (let year = currentYear; year >= 2000; year--) {
        years.push({ value: year, text: year.toString() })
      }
      return years
    },
    statusChartSeries() {
      if (!this.projects || !this.projects.by_status) return []
      return Object.values(this.projects.by_status)
    },
    statusChartOptions() {
      if (!this.projects || !this.projects.by_status) return {}
      return {
        labels: Object.keys(this.projects.by_status).map(key => this.$t(`project.status.${key}`)),
        colors: ['#f59e0b', '#3b82f6', '#10b981', '#ef4444'],
        legend: {
          position: 'bottom',
        },
        plotOptions: {
          pie: {
            donut: {
              size: '65%',
            },
          },
        },
      }
    },
    typeChartSeries() {
      if (!this.projects || !this.projects.by_type) return []
      return Object.values(this.projects.by_type)
    },
    typeChartOptions() {
      if (!this.projects || !this.projects.by_type) return {}
      return {
        labels: Object.keys(this.projects.by_type).map(key =>
          // this.$t(`project.type.${key}`)
          key),
        colors: ['#3b82f6', '#10b981', '#f59e0b'],
        legend: {
          position: 'bottom',
        },
        plotOptions: {
          pie: {
            donut: {
              size: '65%',
            },
          },
        },
      }
    },
    customerTypeChartSeries() {
      if (!this.projects || !this.projects.by_customer_type) return []
      return Object.values(this.projects.by_customer_type)
    },
    customerTypeChartOptions() {
      if (!this.projects || !this.projects.by_customer_type) return {}
      return {
        labels: Object.keys(this.projects.by_customer_type).map(key => this.$t(`customer.type.${key}`)),
        colors: ['#3b82f6', '#10b981'],
        legend: {
          position: 'bottom',
        },
        plotOptions: {
          pie: {
            donut: {
              size: '65%',
            },
          },
        },
      }
    },
  },
  methods: {
    ...mapActions('dashboard', ['fetchProjects', 'refreshWidget']),
    onPeriodChange() {
      if (this.selectedPeriod === 'quarter' && !this.selectedQuarter) {
        this.selectedQuarter = Math.ceil((new Date().getMonth() + 1) / 3)
      }
      this.onFilterChange()
    },
    onFilterChange() {
      const params = { period: this.selectedPeriod }

      if (this.selectedPeriod === 'quarter') {
        params.quarter = this.selectedQuarter
        params.year = this.selectedYear
      } else if (this.selectedPeriod === 'year') {
        params.year = this.selectedYear
      }

      this.fetchProjects(params)
    },
    applyCustomDateRange() {
      if (this.customStartDate && this.customEndDate) {
        const params = {
          start_date: this.customStartDate,
          end_date: this.customEndDate,
        }
        this.fetchProjects(params)
        this.selectedPeriod = 'month' // Reset to show custom range is active
      }
    },
    resetCustomDateForm() {
      this.customStartDate = ''
      this.customEndDate = ''
    },
    refreshData() {
      const params = { period: this.selectedPeriod }

      if (this.selectedPeriod === 'quarter') {
        params.quarter = this.selectedQuarter
        params.year = this.selectedYear
      } else if (this.selectedPeriod === 'year') {
        params.year = this.selectedYear
      }

      this.refreshWidget({ widget: 'projects', params })
    },
    getStatusVariant(status) {
      const variants = {
        pending: 'warning',
        in_progress: 'primary',
        completed: 'success',
        cancelled: 'danger',
      }
      return variants[status] || 'secondary'
    },
    formatCurrency(value) {
      return new Intl.NumberFormat('ar-EG', {
        style: 'currency',
        currency: 'EGP',
      }).format(value)
    },
  },
  created() {
    this.selectedPeriod = this.filters.projectsPeriod || 'month'
    this.selectedQuarter = this.filters.projectsQuarter || Math.ceil((new Date().getMonth() + 1) / 3)
    this.selectedYear = this.filters.projectsYear || new Date().getFullYear()
  },
}
</script>

<style scoped>
.animate-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
